import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

const PROMPT_SHOWN_KEY = '_pwaInstallPrompted';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

/**
 * Captures the browser's `beforeinstallprompt` event (Android/Chrome/Edge) so it can be
 * replayed later from our own modal instead of the browser's generic mini-infobar. Must be
 * instantiated as early as possible (AppComponent injects this eagerly in its constructor)
 * since the event fires once, early in the page lifecycle, and is lost if nothing is
 * listening yet. iOS Safari never fires this event — there is no native install API there,
 * so installability is offered via a manual "Add to Home Screen" guide instead.
 */
@Injectable({ providedIn: 'root' })
export class PwaInstallService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private installed = false;

  constructor() {
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
    });
    window.addEventListener('appinstalled', () => {
      this.installed = true;
      this.deferredPrompt = null;
    });
  }

  /** True if already running as an installed PWA (standalone display mode). */
  isStandalone(): boolean {
    return window.matchMedia?.('(display-mode: standalone)').matches
      || (navigator as any).standalone === true;
  }

  /** Android/Chrome/Edge only — a real native install prompt is available to trigger. */
  canPromptInstall(): boolean {
    return !!this.deferredPrompt;
  }

  isIos(): boolean {
    return /iphone|ipad|ipod/i.test(navigator.userAgent) && !(window as any).MSStream;
  }

  isSafari(): boolean {
    const ua = navigator.userAgent;
    return /safari/i.test(ua) && !/chrome|crios|fxios|edgios/i.test(ua);
  }

  async shouldOfferInstall(): Promise<boolean> {
    if (this.installed || this.isStandalone()) {
      return false;
    }
    const shown = await Preferences.get({ key: PROMPT_SHOWN_KEY });
    if (shown.value === 'true') {
      return false;
    }
    return this.canPromptInstall() || (this.isIos() && this.isSafari());
  }

  async markPrompted(): Promise<void> {
    await Preferences.set({ key: PROMPT_SHOWN_KEY, value: 'true' });
  }

  /** Triggers the real native install prompt (Android/Chrome/Edge only). Returns null when unavailable (e.g. the iOS manual-guide path). */
  async promptInstall(): Promise<'accepted' | 'dismissed' | null> {
    if (!this.deferredPrompt) {
      return null;
    }
    const promptEvent = this.deferredPrompt;
    this.deferredPrompt = null;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    return choice.outcome;
  }
}
